/**
 * Web Push helpers — subscribe / unsubscribe / permission plumbing.
 *
 * The Web Push API is only reliably available when:
 *   - The page is served over HTTPS
 *   - A service worker is registered and active
 *   - The browser supports both PushManager AND Notification (iOS 16.4+ counts)
 *
 * On iOS specifically, Push only works when the app has been added to the
 * home screen and launched in standalone mode — Safari itself won't fire pushes.
 */
import { api } from "@/lib/api";

// Public VAPID keys are base64url-encoded, but PushManager wants raw bytes.
function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  if (typeof window === "undefined") return false;
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function isStandalonePWA() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

/** Ask the OS for permission. Returns the resulting permission state. */
export async function requestPermission() {
  if (!("Notification" in window)) return "denied";
  const p = await Notification.requestPermission();
  return p; // "granted" | "denied" | "default"
}

async function getRegistration() {
  const reg = await navigator.serviceWorker.getRegistration();
  if (reg) return reg;
  // Fall back to explicit register (e.g. in dev mode where auto-register is off).
  return navigator.serviceWorker.register("/service-worker.js");
}

/** Subscribe the current browser to push and register with the backend. */
export async function subscribe() {
  if (!isPushSupported()) throw new Error("Push not supported in this browser");

  const permission = await requestPermission();
  if (permission !== "granted") {
    throw new Error(
      permission === "denied"
        ? "Notifications are blocked. Enable them in browser settings and try again."
        : "Notification permission not granted"
    );
  }

  const reg = await getRegistration();
  const { data } = await api.get("/push/vapid-public-key");
  const applicationServerKey = urlBase64ToUint8Array(data.key);

  // Reuse existing subscription if the browser already has one under the same key.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  }

  // Ship it to the backend so the server can send us pushes.
  const raw = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: raw.endpoint,
    keys: raw.keys,
    user_agent: navigator.userAgent,
  });
  return sub;
}

/** Unsubscribe from this browser + tell the backend to forget the endpoint. */
export async function unsubscribe() {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch {
    // Continue anyway — we still want to clear the server-side record.
  }
  await api.post("/push/unsubscribe", { endpoint });
}

/** Is this browser currently subscribed (regardless of backend state)? */
export async function currentSubscription() {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}
